import { Button } from '@/global/components/ui/button'
import { cn } from '@/lib/utils'
import { MinusIcon, PlusIcon } from 'lucide-react'
import { optionsColumn } from '../hooks/useDataGridPro'
import { useTableApiContext } from '../utils/table-context'
import {
    GridColDefPro,
    GridRenderCellParams,
    GridRenderHeaderParams,
} from '../utils/types'

// Os dois ícones ficam sobrepostos na mesma célula do grid para a troca ser um fade.
function ExpandIcon({ isExpanded }: { isExpanded: boolean }) {
    return (
        <span className="grid *:col-start-1 *:row-start-1 *:transition-opacity *:duration-200">
            <PlusIcon className={cn(isExpanded && 'opacity-0')} />
            <MinusIcon className={cn(!isExpanded && 'opacity-0')} />
        </span>
    )
}

function ExpandCell({ id, tableRow: rowDaCelula }: GridRenderCellParams) {
    const { table, enableRowExpanding, enableRowGrouping } =
        useTableApiContext()
    const tableRow =
        rowDaCelula ??
        table?.getRowModel().flatRows.find(r => r.original.id === String(id))

    if (
        !tableRow ||
        !(enableRowExpanding || enableRowGrouping) ||
        !tableRow.getCanExpand()
    )
        return <></>

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10 rounded-full"
            onClick={tableRow.getToggleExpandedHandler()}
            title={tableRow.getIsExpanded() ? 'Recolher' : 'Expandir'}
        >
            <ExpandIcon isExpanded={tableRow.getIsExpanded()} />
        </Button>
    )
}

function ExpandHeader(_: GridRenderHeaderParams) {
    const { table, enableRowExpanding, enableRowGrouping } =
        useTableApiContext()

    if (!table || !(enableRowExpanding || enableRowGrouping)) return <></>

    /**
     * Com agrupamento nativo não existe "expandir tudo" coerente: quem decide
     * se uma linha abre é o `getRowCanExpand` (o nível terminal é agregado
     * puro, sem drill-down — #1923), e o handler global não passa por ele.
     * `getToggleAllRowsExpandedHandler()` grava `expanded: true` bruto e o
     * `getExpandedRowModel` obedece sem consultar `getRowCanExpand`, então um
     * clique aqui reabriria o bug inteiro. O botão só faz sentido na árvore
     * manual (`enableRowExpanding`), onde a expansão é a própria estrutura das
     * linhas.
     */
    if (enableRowGrouping) return <></>

    const allRowsExpanded = table.getIsAllRowsExpanded()
    const canSomeRowsExpand = table.getCanSomeRowsExpand()

    if (!canSomeRowsExpand) return <></>

    return (
        <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-10 rounded-full"
            onClick={table.getToggleAllRowsExpandedHandler()}
            title={allRowsExpanded ? 'Recolher todos' : 'Expandir todos'}
        >
            <ExpandIcon isExpanded={allRowsExpanded} />
        </Button>
    )
}

/**
 * Coluna que controla a expansão de linhas
 *
 * @example
 *
 * ```tsx
 * const columns: GridColDefPro[] = [
 *      EXPANDABLE_COL,
 *      ...rest,
 * ]
 * ```
 */
export const EXPANDABLE_COL: GridColDefPro = {
    ...optionsColumn,
    field: '__expand',
    renderCell: ExpandCell,
    renderHeader: ExpandHeader,
    width: 50,
    align: 'center',
    headerAlign: 'center',
}
